import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, Lock } from "lucide-react";
import { useSeo } from "../lib/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin login screen — supports:
 *  a) Email + password (new professional flow)
 *  b) Legacy admin key fallback (compatibility)
 *  c) First-time setup (if no admin user exists yet)
 */
export default function AdminLogin() {
  const [mode, setMode] = useState("email"); // email | key | setup
  const [initialized, setInitialized] = useState(true);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [key, setKey] = useState("");
  const navigate = useNavigate();

  useSeo({
    title: "Administration · MAISON DOUBA",
    description: "Espace administrateur MAISON DOUBA",
    canonical: "https://wwwmaisondouba.com/admin/login",
  });

  useEffect(() => {
    (async () => {
      try {
        // Already logged in?
        await axios.get(`${API}/admin/whoami`, { withCredentials: true });
        navigate("/admin");
        return;
      } catch (e) {
        // 401 expected when not logged; other errors are non-blocking
        if (e?.response && e.response.status !== 401) console.debug("[AdminLogin] whoami:", e.message);
      }
      try {
        const { data } = await axios.get(`${API}/admin/setup/status`);
        setInitialized(data.initialized);
        if (!data.initialized) setMode("setup");
      } catch (e) {
        console.debug("[AdminLogin] setup/status:", e?.message || e);
      }
      finally { setChecking(false); }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "email") {
        await axios.post(`${API}/admin/login-email`, { email, password }, { withCredentials: true });
      } else if (mode === "key") {
        await axios.post(`${API}/admin/login`, { key }, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/setup`, { email, password, full_name: fullName }, { withCredentials: true });
        toast.success("Compte administrateur créé.");
      }
      toast.success("Connecté");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connexion impossible");
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center"><Loader2 className="animate-spin text-[color:var(--gold)]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <p className="label-caps text-[color:var(--gold)] text-[11px] tracking-[0.3em] mb-4 flex items-center justify-center gap-2">
            <Lock size={14} /> Espace administrateur
          </p>
          <h1 className="font-serif text-5xl mb-3">MAISON DOUBA</h1>
          <p className="text-white/50 text-sm">
            {mode === "setup" ? "Créez votre compte administrateur" : "Connexion à votre back office"}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6">
          {mode === "setup" && (
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Votre nom</label>
              <input
                data-testid="admin-full-name"
                required minLength={2} value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none"
              />
            </div>
          )}
          {(mode === "email" || mode === "setup") && (
            <>
              <div>
                <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Email</label>
                <input
                  data-testid="admin-email"
                  required type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none"
                />
              </div>
              <div>
                <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">
                  Mot de passe {mode === "setup" && "(8 caractères min.)"}
                </label>
                <input
                  data-testid="admin-password"
                  required type="password" minLength={mode === "setup" ? 8 : 1} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none"
                />
              </div>
            </>
          )}
          {mode === "key" && (
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Clé administrateur (legacy)</label>
              <input
                data-testid="admin-key-input"
                required type="password" value={key}
                onChange={(e) => setKey(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none font-mono text-xs"
              />
            </div>
          )}

          <button
            data-testid="admin-login-btn"
            type="submit" disabled={busy}
            className="w-full bg-[color:var(--gold)] text-black py-4 label-caps text-xs tracking-[0.3em] flex items-center justify-center gap-2 hover:bg-white transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : (
              mode === "setup" ? <>Créer mon compte <ArrowRight size={14} /></> : <>Se connecter <ArrowRight size={14} /></>
            )}
          </button>
        </form>

        {initialized && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setMode(mode === "email" ? "key" : "email")}
              className="text-[color:var(--gold)] hover:text-white transition-colors label-caps text-[10px] tracking-[0.25em]"
              data-testid="toggle-auth-mode"
            >
              {mode === "email" ? "→ Connexion par clé (legacy)" : "→ Connexion par email"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
